import { type MinStockValuesDto, useUpdateOneMinStock } from '@gozoki/api';
import { Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import DailySalesTextField from './DailySalesTextField';

export interface DailySalesGridRowProps {
    storeId: number;
    productReference: string;
    label: string;
    minStockValues: MinStockValuesDto;
    avgValues: number[];
    edit: boolean;
    disabled: boolean;
    autoCalculate: boolean;
    onModify: (reference: string) => void;
    onSave: (reference: string) => void;
    onCancel: (reference: string) => void;
    calculateMinimumStock: (reference: string) => number[];
    needSync: boolean;
}

export const DailySalesGridRow = (props: DailySalesGridRowProps) => {
    const {
        storeId,
        productReference,
        minStockValues,
        avgValues,
        label,
        edit,
        disabled,
        autoCalculate,
        onModify,
        onSave,
        onCancel,
        calculateMinimumStock,
        needSync,
    } = props;
    const { mutate: updateMinStock } = useUpdateOneMinStock();
    const [editValues, setEditValues] = useState<MinStockValuesDto>(minStockValues);

    const handleSave = useCallback(() => {
        if (!editValues) return;
        updateMinStock(
            {
                storeId,
                productReference,
                minStockValues: { ...editValues },
            },
            { onSuccess: () => onSave(productReference) }
        );
    }, [editValues, updateMinStock, onSave, productReference, storeId]);

    const handleModify = useCallback(
        (reference: string) => {
            setEditValues(minStockValues);
            onModify(reference);
        },
        [minStockValues, onModify]
    );

    const handleCancel = useCallback(() => {
        setEditValues(minStockValues);
        onCancel(productReference);
    }, [minStockValues, onCancel, productReference]);

    useEffect(() => {
        if (autoCalculate) {
            onModify(productReference);
            const calculatedMinStockValues = calculateMinimumStock(productReference);
            const minStockValuesDto = {
                minimumStock: 0,
                weekEndMinimumStock: calculatedMinStockValues[5],
                mondayMinimumStock: calculatedMinStockValues[0],
                tuesdayMinimumStock: calculatedMinStockValues[1],
                wednesdayMinimumStock: calculatedMinStockValues[2],
                thursdayMinimumStock: calculatedMinStockValues[3],
                fridayMinimumStock: calculatedMinStockValues[4],
                saturdayMinimumStock: calculatedMinStockValues[5],
                sundayMinimumStock: calculatedMinStockValues[6],
            };
            setEditValues(minStockValuesDto);
        }
    }, [minStockValues, autoCalculate, calculateMinimumStock, productReference, onModify]);

    useEffect(() => {
        if (needSync) {
            handleSave();
        }
    }, [needSync, handleSave, productReference]);

    return (
        <Stack borderBottom="1px solid black">
            <Stack flexDirection="row" gap={1} py={2} alignItems="center">
                <Stack flex={2} px={1}>
                    <Typography variant="body1">{label}</Typography>
                </Stack>
                {avgValues.map((value, index) => {
                    return (
                        <Stack flex={1} key={`avg-${value}-${index}`}>
                            <Typography>{value}</Typography>
                        </Stack>
                    );
                })}
                <Stack flex={2} />
            </Stack>
            <Stack flexDirection="row" gap={1} py={2} alignItems="center" bgcolor="#F6F6F6">
                <Stack flex={2} px={1}>
                    <Typography variant="body2" fontWeight={600}>
                        Stock minimum
                    </Typography>
                </Stack>
                {[
                    'mondayMinimumStock',
                    'tuesdayMinimumStock',
                    'wednesdayMinimumStock',
                    'thursdayMinimumStock',
                    'fridayMinimumStock',
                    'weekEndMinimumStock',
                    'sundayMinimumStock',
                ].map((dayKey) => (
                    <DailySalesTextField
                        edit={edit}
                        editValues={editValues}
                        minStockValues={minStockValues}
                        setEditValues={setEditValues}
                        dayKey={dayKey as keyof MinStockValuesDto}
                    />
                ))}
                <Stack flex={2} alignItems="center">
                    {edit ? (
                        <Stack flexDirection="row" gap={1}>
                            <Button onClick={handleSave} variant="contained">
                                Enregistrer
                            </Button>
                            <Button onClick={handleCancel} variant="contained" color="error">
                                Annuler
                            </Button>
                        </Stack>
                    ) : (
                        <Button
                            disabled={disabled}
                            onClick={() => handleModify(productReference)}
                            variant="outlined"
                            color="secondary"
                        >
                            Modifier
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};
